/**
 * English line-plot prompts and data titles (ADR 0084 §5).
 *
 * The prompts name the mark ("X's" or "dots"), not a noun from the data, so
 * one sentence fits every context. The title above the plot says what the
 * data is.
 */
/** The mark's `LinePlotMarkOptions` value: 'X' or 'Dot'. */
type Mark = string;

const markWord = (mark: Mark) => (mark === 'Dot' ? 'dots' : "X's");

export const linePlotPrompts = {
    readOne: (value: string, mark: Mark) => `How many ${markWord(mark)} are above ${value}?`,
    difference: (more: string, fewer: string, mark: Mark) =>
        `How many more ${markWord(mark)} are above ${more} than above ${fewer}?`,
    total: (mark: Mark) => `How many ${markWord(mark)} are there in all?`,
    mostCommon: () => `Which value appears most often?`,
    range: () => `What is the greatest value minus the least value?`,
    median: () => `What is the median of the data?`,
};

/**
 * What a plot's data could be, and the whole number its line may start at.
 * `start` keeps a context plausible: nobody has 14 pets. Counted data is for
 * whole-number plots; measured data for halves, quarters and eighths, whose
 * line spans four, two or one units from `start`.
 */
export const linePlotContexts: {
    counted: { title: string; start: [number, number] }[];
    measured: { title: string; start: [number, number] }[];
} = {
    counted: [
        { title: 'Books read by each student this month', start: [0, 4] },
        { title: 'Goals scored in each game', start: [0, 1] },
        { title: 'Letters in each name', start: [2, 4] },
        { title: 'Points scored in each round', start: [0, 12] },
        { title: 'Push-ups each student did', start: [5, 12] },
    ],
    measured: [
        { title: 'Lengths of ribbons (inches)', start: [1, 6] },
        { title: 'Lengths of pencils (inches)', start: [2, 5] },
        { title: 'Lengths of worms (inches)', start: [1, 3] },
        { title: 'Weights of puppies (pounds)', start: [2, 8] },
        { title: 'Water in each bottle (cups)', start: [0, 2] },
        { title: 'Distance each student ran (miles)', start: [0, 3] },
    ],
};
